import "./styles.css";

import { assetIndexFile, idfExamplesDir } from "idfweb-assets";
import { GUEST_PROJECT, tableOffsetOf } from "idfweb-build";
import { chipNameOfTarget } from "idfweb-chip";
import { fetchExample, fetchExampleIndex, type ExampleIndex, type Project } from "idfweb-project";
import type { PageToRuntime, RuntimeToPage } from "idfweb-runtime-protocol";
import { FsClient } from "idfweb-runtime-protocol/fs-rpc";
import { createReporter, TaskView, type Reporter, type Task } from "idfweb-tasks";
import { ToolRegistry } from "idfweb-tools";
import { createWorkbench, keyLabel, type Workbench } from "idfweb-workbench";

import { AgentLink, createAgentButton } from "./agent.ts";
import {
    chosenToolchains,
    configurationName,
    defaultTarget,
    emulatorEnabled,
    foldersOffered,
    idfExampleShown,
    idfRelease,
    idfExamplesOffered,
    loadDeployment,
    type StartPage,
    shownTargets,
    suggestedIdfExamples,
    targetShown,
} from "./deployment.ts";
import { createDevicePane, iconButton, type DevicePane } from "./device.ts";
import { DeviceExplorer } from "./device-explorer.ts";
import { emulatorConnector } from "./emulator-board.ts";
import { createGuide, linksProjects } from "./guide.ts";
import { addGuideTools, PageConfigs } from "./guide-tools.ts";
import { copyPage } from "./example-copy.ts";
import { renderExamplePicker, type ExampleSet, type PickerExample } from "./example-picker.ts";
import { createHelpButton, createIntro, missingCapabilities, showIntroAtStart } from "./intro.ts";
import { createKeptProjects, type KeptProjects } from "./kept-projects.ts";
import { allowedAccess, localDirectoriesSupported, pickDirectory } from "./local-directory.ts";
import { nvsView } from "./nvs-view.ts";
import { addWaitTool, PageEvents } from "./page-events.ts";
import {
    lastLayout,
    storeBoard,
    storedBoard,
    storedEditors,
    storedLayout,
    storedPreset,
    storedRoot,
    storedTarget,
    storeEditors,
    storeLayout,
    storePreset,
    storeRoot,
    storeTarget,
    type Layout,
} from "./project-state.ts";
import { createPlaceButton } from "./project-place.ts";
import { exampleLink, exampleOf, type ExampleSetName, pagePath, projectIdOf } from "./project-url.ts";
import { serialConnector } from "./serial-board.ts";
import { askAboutConflicts, askToSync } from "./sync-dialogs.ts";
import { autoTarget, setAutoTarget } from "./target.ts";
import { createThemeButton, installTheme, type ThemeController } from "./theme.ts";
import { createToasts } from "./toasts.ts";
import { addPageTools, PageBuilds, PageCommands, PageProjects } from "./tools.ts";
import { usbConnector } from "./usb-board.ts";
import { createWelcome, type WelcomeItem } from "./welcome.ts";

/**
 * The page: a toolbar over the workbench on the left and the output panel on
 * the right, the runtime worker underneath, and the welcome window over them
 * until a project is chosen.
 *
 * Every path here is resolved against `document.baseURI` rather than written
 * as a URL, because the server cannot know the origin — it may be speaking
 * plain HTTP to a reverse proxy on localhost while the browser is on an HTTPS
 * name.
 *
 * This is also where the two halves are introduced. The worker owns the guest
 * filesystem; the workbench is a client of it over a MessagePort handed to it
 * at construction. Nothing is relayed through here.
 */

/** The output panel's tab for the board's serial output. */
const MONITOR = "Serial Monitor";

/** Neither pane is worth having narrower than this. */
const MIN_PANE = 280;

const paths = document.getElementById("idfweb-paths")!.dataset as Record<string, string>;
const toolbarElement = document.getElementById("toolbar")!;
const workbenchElement = document.getElementById("workbench")!;
const splitterElement = document.getElementById("splitter")!;
const sideElement = document.getElementById("side")!;

/** A site path — "./static/page" — as a URL, wherever the site is rooted. */
const site = (path: string) => new URL(path, document.baseURI).toString();

/**
 * The example being opened: the task it is reported under, ended by the
 * worker's answer, and the chips its README offers, which the answer applies.
 * A directory is opened under a task of the worker's own.
 */
let opening: { task: Task; targets: string[] } | undefined;

/** What the tab's title ends with: the deployment's title, or idfweb. */
let siteTitle = "idfweb";

/** The deployment's configuration first: it decides what main() puts on the page. */
async function start(): Promise<void> {
    const { problem, start } = await loadDeployment(
        site(paths.configs!),
        configurationName(location.pathname),
        paths.github ? site(paths.github) : undefined,
    );
    setAutoTarget(defaultTarget());
    main(problem, start);
}

function main(configurationProblem: string | undefined, startPage: StartPage): void {
    if (startPage.title !== undefined) document.title = siteTitle = startPage.title;
    const theme = installTheme();
    const split = splitter(() => saveLayout());

    if (!globalThis.crossOriginIsolated) {
        // Nothing here works without it: the guest filesystem is a growable
        // SharedArrayBuffer and every process is a worker sharing it. Said
        // plainly, and instead of the rest, because the alternative is a
        // workbench that comes up and then waits forever on a workspace whose
        // filesystem could not be created.
        fatal(
            "This page is not cross-origin isolated, so nothing here can run. " +
                "It has to be served with COOP and COEP — see packages/web/src/server.ts.",
        );
        return;
    }

    // The right-hand column is the output panel, with the serial monitor, the
    // build output and clangd's log. The toasts go over the workbench's bottom
    // left corner.
    const tasks = new TaskView();
    const toasts = createToasts(document.body, tasks, workbenchElement);
    const panelHost = document.createElement("div");
    panelHost.className = "panel-host";
    sideElement.appendChild(panelHost);
    const monitor = document.createElement("div");
    // The page's own reporter, beside the worker's: a flash runs here, because
    // the serial port does. Both streams end up in the toasts.
    const reporter = createReporter((event) => tasks.apply(event), "page");
    if (configurationProblem !== undefined) reporter.message("error", configurationProblem);
    const { worker, forWorkbench } = startRuntime();
    const builds = new PageBuilds((message) => worker.postMessage(message));
    const projects = new PageProjects((message) => worker.postMessage(message));
    /** The project root builds use and its build directory, as the worker last said. */
    let projectRoot = GUEST_PROJECT;
    let buildDir = `${GUEST_PROJECT}/build`;
    const commands = new PageCommands((message) => worker.postMessage(message));
    const configs = new PageConfigs((message) => worker.postMessage(message));
    const pageFs = new FsClient((message, transfer) => worker.postMessage(message, transfer));
    const registry = new ToolRegistry();
    const events = new PageEvents();
    const agent = new AgentLink(registry);
    // The layout is stored as it changes, from when the one stored before is shown.
    let tracking = false;
    let sidebarShown = true;
    let sidebarWidth: number | undefined;
    let panelTab: string | undefined = MONITOR;
    const kept = createKeptProjects({
        post: (message) => worker.postMessage(message),
        reporter,
        configuration: configurationName(location.pathname),
        showPage: (page) => welcome.show(page),
        changed: (record) => {
            agent.setProject(record && { id: record.id, name: record.name, folder: record.handle?.name });
            place.show(record);
            if (record === undefined) return;
            showProjectName(record.name);
            storeTarget(device.target());
            const board = device.remembered();
            if (board !== undefined) storeBoard(board);
            // A project kept before has a layout and tabs of its own; a new one takes what is shown.
            const own = storedLayout();
            if (own !== undefined) applyLayout(own);
            else saveLayout();
            if (storedEditors() === undefined) storeEditors(workbench.openEditors());
        },
        taken: () => device.disconnect(),
    });
    const place = createPlaceButton(kept, reporter);

    // The side bars' buttons, drawn as shown or hidden; the workbench says which the left one is.
    const leftToggle = toggleButton(
        "layout-sidebar-left",
        () => void workbench.execute("workbench.action.toggleSidebarVisibility"),
    );
    const rightToggle = toggleButton("layout-sidebar-right", () => toggleSide());
    const workbench = createWorkbench(workbenchElement, {
        port: forWorkbench,
        reporter,
        ask: toasts.ask,
        flash: () => device.flash(),
        panelHost,
        panelShown: () => {
            if (sideElement.hidden) toggleSide();
        },
        sidebarChanged: (visible) => {
            leftToggle.show(visible);
            // The drawer of a narrow screen is not the side bar a wider one shows.
            if (!split.stacked.matches) sidebarShown = visible;
            saveLayout();
        },
        sidebarResized: (width) => {
            sidebarWidth = width;
            saveLayout();
        },
        savedEditors: () => storedEditors(),
        editorsChanged: (editors) => storeEditors(editors),
        action: (href, title, dir) => guide.run(href, title, dir),
        event: (event) => {
            if (event.kind === "quick") events.emit({ kind: "palette", prefix: event.prefix });
            else if (event.kind === "built") events.emit({ ...event, kind: "build", target: device.target() });
            else events.emit(event);
        },
        chooseProject: async (top, choices) => {
            const stored = storedRoot();
            if (stored !== undefined && choices.includes(stored))
                return (await projects.select(stored)).error === undefined;
            // A README linking to the tree's projects identifies the entry point.
            const readme = await pageFs.readFile(`${top}/README.md`).catch(() => undefined);
            return readme !== undefined && linksProjects(new TextDecoder().decode(readme));
        },
    });
    const guide = createGuide(workbench, reporter, {
        reveal: (element) => {
            if (sideElement.contains(element) && sideElement.hidden) toggleSide();
            if (element.closest(".wb-sidebar") !== null) workbench.setSidebar(true);
        },
        sdkconfig: () => `${projectRoot}/sdkconfig`,
        configure: async () => (await configs.configure()).error,
        revealable: (element) => {
            // Only what the hidden column or side bar alone hides, not a view inactive inside it.
            const root = sideElement.contains(element) ? sideElement : element.closest(".wb-sidebar");
            for (let at: Element | null = element; at !== null && at !== root; at = at.parentElement) {
                if (at instanceof HTMLElement && (at.hidden || getComputedStyle(at).display === "none")) return false;
            }
            return root !== null;
        },
        setLayout: (preset) => applyLayout({ ...currentLayout(), ...preset, tab: preset.tab ?? panelTab }),
        selectProject: async (path) => (await projects.select(path)).error,
    });
    workbench.panel.addView(MONITOR, monitor);
    workbench.panel.onSelect((tab) => {
        panelTab = tab;
        saveLayout();
    });

    const build = buildButtons(reporter, workbench);
    const emulator = emulatorConnector({
        run: (target, image, flashBytes) => {
            const channel = new MessageChannel();
            const message: PageToRuntime = { type: "emulate", port: channel.port2, target, image, flashBytes };
            worker.postMessage(message, [channel.port2, image.buffer]);
            return channel.port1;
        },
    });
    // The device pane's controls go in the page's toolbar, and its dialogs in it too.
    const device = createDevicePane(toolbarElement, {
        connectors: emulatorEnabled.get() ? [serialConnector, usbConnector, emulator] : [serialConnector, usbConnector],
        readFiles: (paths) => Promise.all(paths.map((path) => pageFs.readFile(path))),
        reporter,
        buildDir: () => buildDir,
        setTarget: (target, download) => {
            storeTarget(target);
            events.emit({ kind: "target", target });
            const message: PageToRuntime = { type: "target", target, download };
            worker.postMessage(message);
        },
        setPreset: (name) => {
            const message: PageToRuntime = { type: "preset", name };
            worker.postMessage(message);
        },
        terminalHost: monitor,
        rememberedChanged: (board) => storeBoard(board),
        connectionChanged: (connected) => {
            build.setConnected(connected);
            // The pane draws its toolbar while it is created, before the explorer below exists.
            queueMicrotask(() => deviceChanged());
        },
        // The app's output follows the flash in the monitor.
        flashStarted: () => workbench.panel.show(MONITOR),
        flashEnded: (failure) => {
            deviceExplorer.forget();
            const board = device.board()?.label ?? "The board";
            events.emit({ kind: "flash", ok: failure === undefined, board, error: failure });
        },
    });
    const deviceExplorer = new DeviceExplorer({
        board: () => device.board(),
        disks: () => device.board()?.disks?.() ?? [],
        read: (disk, offset, length, title) => device.readDisk(disk, offset, length, title),
        tableOffset: async () => {
            const json = await pageFs.readFile(`${buildDir}/flasher_args.json`).catch(() => undefined);
            return json === undefined ? undefined : tableOffsetOf(new TextDecoder().decode(json));
        },
        changed: () => workbench.refreshExplorer(),
        putFile: async (path, data) => {
            // Each folder on the way may exist already; a real failure shows in the write.
            const parts = path.split("/").slice(1, -1);
            for (let i = 1; i <= parts.length; i++)
                await pageFs.mkdir(`/${parts.slice(0, i).join("/")}`).catch(() => {});
            await pageFs.writeFile(path, data, { create: true, overwrite: true });
        },
        openNvs: (tab) => workbench.openView(nvsView(tab)),
        openFile: (path) => void workbench.open(path),
    });
    workbench.setPageSections([deviceExplorer.section()]);
    // The toolbar is redrawn for every step of the board's work; the explorer only for a new board.
    let shownBoard: { board: object | undefined; ready: boolean } = { board: undefined, ready: false };
    let announcedBoard: object | undefined;
    const deviceChanged = () => {
        const board = device.board();
        const ready = board?.ready ?? false;
        if (board === shownBoard.board && ready === shownBoard.ready) return;
        shownBoard = { board, ready };
        // A board that comes back after its reset is not a new connection.
        if (ready && board !== announcedBoard) events.emit({ kind: "board", label: board!.label });
        if (ready) announcedBoard = board;
        workbench.refreshExplorer();
    };
    const command = (icon: string, id: string, prefix: string) => commandButton(workbench, icon, id, prefix);
    const toggleSide = () => {
        const visible = sideElement.hidden;
        sideElement.hidden = !visible;
        splitterElement.hidden = !visible;
        rightToggle.show(visible);
        saveLayout();
    };
    workbench.register(
        {
            id: "workbench.action.toggleAuxiliaryBar",
            title: "Toggle Right Side Bar Visibility",
            category: "View",
            keys: ["Mod-Alt-b"],
            run: toggleSide,
        },
        {
            id: "idfweb.openProject",
            title: "Open Project…",
            category: "File",
            keys: ["Mod-o"],
            run: () => welcome.show(),
        },
    );
    leftToggle.button.title = describeCommand(workbench, "workbench.action.toggleSidebarVisibility");
    rightToggle.button.title = describeCommand(workbench, "workbench.action.toggleAuxiliaryBar");
    rightToggle.show(false);

    // Declared as functions: the workbench reports its side bar while it is created.
    function saveLayout(): void {
        if (tracking) storeLayout(currentLayout());
    }
    function currentLayout(): Layout {
        const side = !sideElement.hidden;
        return { sidebar: sidebarShown, sidebarWidth, side, tab: panelTab, split: split.size() };
    }
    function applyLayout(layout: Layout): void {
        tracking = false;
        if (!split.stacked.matches) workbench.setSidebar(layout.sidebar !== false);
        if (typeof layout.sidebarWidth === "number") {
            sidebarWidth = layout.sidebarWidth;
            workbench.setSidebarWidth(sidebarWidth);
        }
        if (layout.side === sideElement.hidden) toggleSide();
        if (typeof layout.tab === "string") workbench.panel.select(layout.tab);
        panelTab = layout.tab ?? panelTab;
        split.setSize(layout.split);
        tracking = true;
        saveLayout();
    }
    toolbarElement.append(
        iconButton("folder-opened", "Open a project", () => welcome.show()),
        place.element,
        createAgentButton(agent),
        separator(),
        device.controls.target,
        build.element,
        device.controls.connection,
        spacer(),
        leftToggle.button,
        rightToggle.button,
        shortcutsButton(workbench),
        separator(),
        ...navigationButtons(workbench),
        command("terminal", "workbench.action.showCommands", ">"),
        command("go-to-file", "workbench.action.quickOpen", ""),
        command("symbol-class", "workbench.action.showAllSymbols", "#"),
        separator(),
        createThemeButton(theme),
        iconButton(
            "settings-gear",
            describeCommand(workbench, "workbench.action.openSettings"),
            () => void workbench.execute("workbench.action.openSettings"),
        ),
        createHelpButton(() => welcome.show(intro)),
        separator(),
        device.controls.status,
    );
    addPageTools(registry, { builds, projects, commands, fs: pageFs, device });
    addGuideTools(registry, { guide, workbench, configs, fs: pageFs, buildDir: () => buildDir });
    addWaitTool(registry, { events, board: () => device.board() });
    device.watchSerial((text) => events.serial(text));

    // The example sets are listed from their indexes alone; an example's files
    // are fetched when it is chosen. ESP-IDF's index is fetched once: it is a
    // few hundred kilobytes and the window is shown more than once.
    let idfIndex: Promise<ExampleIndex> | undefined;
    // A release the configuration chose has its own examples, beside the default ones.
    const release = idfRelease();
    const idfExamplesPath = !paths.idfExamples
        ? ""
        : release === undefined
          ? paths.idfExamples
          : `./${idfExamplesDir(release)}`;
    const configuration = configurationName(location.pathname);
    const openNew = (set: ExampleSetName, path: string, name: string, targets: string[] | undefined) =>
        void openExample(worker, reporter, kept, path, name, targets, exampleLink(configuration, set, name));
    // An example this browser keeps a copy of asks first whether to continue with that copy.
    const open = (set: ExampleSetName, path: string) => (name: string, targets: string[] | undefined) =>
        void kept.copyOf(exampleLink(configuration, set, name)).then((record) => {
            if (record === undefined) openNew(set, path, name, targets);
            else
                welcome.show(
                    copyPage({
                        kept: record,
                        proceed: () => void kept.openKept(record),
                        startOver: () => openNew(set, path, name, targets),
                    }),
                );
        });
    const allIdfExamples = (): Promise<ExampleIndex> =>
        (idfIndex ??= fetchExampleIndex(site(idfExamplesPath)).catch((e: unknown) => {
            idfIndex = undefined;
            throw e;
        }));
    // The deployment's filter applies to ESP-IDF's examples; its suggestions are a set of their own.
    const idfExamples = async (): Promise<ExampleIndex> => {
        const index = await allIdfExamples();
        return { examples: index.examples.filter((example) => idfExampleShown(example.name)) };
    };
    const exampleSets: ExampleSet[] = [];
    const own = startPage.examples;
    if (own !== undefined) {
        exampleSets.push({
            label: own.label,
            examples: async () => pickerExamples(await fetchExampleIndex(own.url), open("builtin", own.url)),
        });
    }
    const idfOffered = idfExamplesPath && idfExamplesOffered.get();
    const suggested = suggestedIdfExamples();
    if (idfOffered && suggested.length > 0) {
        exampleSets.push({
            label: "Suggested examples",
            examples: async () => {
                const index = await idfExamples();
                const examples = suggested.flatMap((name) => index.examples.filter((each) => each.name === name));
                return pickerExamples({ examples }, open("esp-idf", idfExamplesPath), index);
            },
        });
    }
    if (idfOffered) {
        exampleSets.push({
            label: "ESP-IDF examples",
            examples: async () => pickerExamples(await idfExamples(), open("esp-idf", idfExamplesPath)),
        });
    }
    const welcome = createWelcome(document.body, async () => [
        ...(await kept.welcomeItems()),
        ...(foldersOffered.get() ? localItems(reporter, kept) : []),
        ...(exampleSets.length === 0
            ? []
            : [
                  {
                      heading: "Start with an example",
                      render: (close: () => void) => renderExamplePicker(exampleSets, close),
                  },
              ]),
    ]);
    // A link, and the configuration's start example, open an example whether the window offers the set or not.
    const sets: Record<ExampleSetName, [string, () => Promise<ExampleIndex>] | undefined> = {
        "esp-idf": idfExamplesPath ? [idfExamplesPath, allIdfExamples] : undefined,
        builtin: own === undefined ? undefined : [own.url, () => fetchExampleIndex(own.url)],
    };
    const linkedExample = (link: { set: ExampleSetName; path: string }) => {
        const set = sets[link.set];
        const found = set === undefined ? Promise.resolve(undefined) : findExample(...set, link.path);
        return found.then((example) => {
            if (example === undefined)
                reporter.message("error", `The example ${link.set}/${link.path} is not on this site.`);
            return example;
        });
    };
    const startExample = startPage.start;
    const intro = createIntro(
        startPage,
        startExample && {
            kept: () => kept.copyOf(exampleLink(configuration, startExample.set, startExample.path)),
            proceed: (record) => void kept.openKept(record),
            startOver: () =>
                void linkedExample(startExample).then((found) => {
                    if (found !== undefined) openNew(startExample.set, found.path, startExample.path, found.targets);
                }),
        },
    );
    // With a start example, the introduction is where the page starts.
    const showWelcome = () => welcome.show(startExample !== undefined || showIntroAtStart() ? intro : undefined);
    // The project or the example in the page's address opens in place of the
    // welcome window, unless the browser lacks something and the introduction
    // has to say what.
    const keptId = projectIdOf(location.pathname);
    const linked = exampleOf(location.pathname);
    const layout = (keptId === undefined ? undefined : storedLayout(keptId)) ?? lastLayout();
    if (layout !== undefined) applyLayout(layout);
    tracking = true;
    if (keptId !== undefined && missingCapabilities().length === 0) {
        void kept.reopen(keptId).then((opening) => {
            if (!opening) showWelcome();
        });
    } else if (linked !== undefined && missingCapabilities().length === 0) {
        void linkedExample(linked).then((found) => {
            if (found !== undefined) return open(linked.set, found.path)(linked.path, found.targets);
            const page = new URL(pagePath(configuration, undefined), document.baseURI);
            history.replaceState(history.state, "", page);
            showWelcome();
        });
    } else {
        showWelcome();
    }

    worker.onmessage = (event: MessageEvent<RuntimeToPage>) => {
        const message = event.data;
        switch (message.type) {
            case "task":
                tasks.apply(message.event);
                return;
            case "targets": {
                const shown = shownTargets(message.targets);
                device.setTargets(shown);
                workbench.setTargets(shown);
                const toolchains: PageToRuntime = { type: "toolchains", toolchains: chosenToolchains(message.targets) };
                worker.postMessage(toolchains);
                return;
            }
            case "opened": {
                if (message.error === undefined) {
                    opening?.task.done();
                    showProjectName(message.name);
                    // The worker took the project's chip; the README's apply when it is not one of them.
                    const offered = opening?.targets ?? [];
                    if (offered.length > 0 && !offered.includes(device.target())) device.selectTarget(offered[0]!);
                } else opening?.task.failed(message.error);
                opening = undefined;
                // A project that sets no chip gets the one chosen for it last.
                const target = message.target ?? (message.error === undefined ? storedTarget() : undefined);
                if (target !== undefined) device.selectTarget(target);
                // A kept project reopening brings back its board, if the browser can find it without asking.
                const board = message.error === undefined ? storedBoard() : undefined;
                if (board !== undefined) device.reconnect(board);
                kept.opened(message.name, message.error);
                return;
            }
            case "conflicts":
                void askAboutConflicts(message.directory, message.conflicts).then((keep) => {
                    const answer: PageToRuntime = { type: "resolved", id: message.id, keep };
                    worker.postMessage(answer);
                });
                return;
            case "fs":
                pageFs.receive(message);
                return;
            case "output":
            case "built":
                if (message.type === "built") {
                    const { ok, elf, target } = message;
                    events.emit({ kind: "build", ok, elf, message: message.message, target });
                }
                builds.receive(message);
                return;
            case "project-root": {
                projectRoot = message.root;
                buildDir = message.buildDir;
                // With a choice to make, the top is a stand-in until a project is chosen.
                if (message.choose === undefined) storeRoot(message.root);
                const stored = storedPreset(message.root);
                if (message.presetChosen === true) storePreset(message.root, message.preset!);
                if (message.presets === undefined) {
                    device.setPresets(undefined, undefined);
                    if (message.target !== undefined) device.selectTarget(message.target);
                } else if (
                    message.presetChosen !== true &&
                    stored !== undefined &&
                    stored !== message.preset &&
                    message.presets.some((each) => each.name === stored)
                ) {
                    // The worker picked as idf.py does; the person's last choice wins.
                    const answer: PageToRuntime = { type: "preset", name: stored };
                    worker.postMessage(answer);
                } else {
                    device.setPresets(message.presets, message.preset);
                }
                return;
            }
            case "projects":
            case "select-project":
            case "set-target":
                projects.receive(message);
                return;
            case "ran":
                commands.receive(message);
                return;
            case "config":
            case "configure":
                configs.receive(message);
                return;
            case "saved":
                kept.receive(message);
                return;
        }
    };

    rescanOnReturn(worker);
}

/**
 * Ask the worker to compare a synced directory with the guest when the user
 * comes back to the page, since what they did elsewhere may have changed it.
 * Focus lands in the workbench's frame as often as in the page.
 */
function rescanOnReturn(worker: Worker): void {
    const rescan = () => {
        const message: PageToRuntime = { type: "rescan" };
        worker.postMessage(message);
    };
    window.addEventListener("focus", rescan);
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") rescan();
    });
}

/**
 * Start the worker that owns the guest, and open the workbench's line to it.
 *
 * A classic worker rather than a module one: it is a self-contained bundle, so
 * a module would buy nothing, and the process workers it starts in turn have to
 * be classic anyway.
 *
 * One end of the channel goes to the worker now and the other to the workbench
 * at construction. From then on the workbench and the worker talk without this
 * page in between, which is what keeps a build's output off the main thread.
 */
function startRuntime(): { worker: Worker; forWorkbench: MessagePort } {
    const worker = new Worker(site(`${paths.page}/runtime-worker.js`), { name: "idfweb-runtime" });
    // A worker that fails to start says so here and nowhere else, and what it
    // takes with it is every message anything is waiting for.
    worker.onerror = (event: ErrorEvent) => console.error("idfweb: the runtime worker failed", event.message, event);

    const channel = new MessageChannel();
    const init: PageToRuntime = {
        type: "init",
        assetsBaseUrl: site(paths.assets!),
        assetsIndex: assetIndexFile(idfRelease()),
        processWorkerUrl: site(`${paths.page}/process-worker.js`),
        workbench: channel.port1,
        target: autoTarget(),
    };
    worker.postMessage(init, [channel.port1]);
    return { worker, forWorkbench: channel.port2 };
}

/**
 * Open one of the examples served with the site, from the set at `path`.
 *
 * The files are fetched here and handed to the worker, which is how every
 * project source works: the page obtains the files — over HTTP, from a
 * directory the user picked, from browser storage — and the worker writes them
 * into the guest. Nothing waits for the toolchain: the worker selects the
 * chip the project sets, and the download starts with the first build.
 *
 * `targets` is the chips the example builds for, when its README says. Once
 * the worker has opened it, the selected chip is kept when it is one of them
 * and replaced by the first of them the deployment offers otherwise.
 */
async function openExample(
    worker: Worker,
    reporter: Reporter,
    kept: KeptProjects,
    path: string,
    name: string,
    targets: string[] | undefined,
    source: string,
): Promise<void> {
    const task = reporter.task(`Opening the ${name} example`);
    opening = { task, targets: targets?.filter(targetShown) ?? [] };
    try {
        const project = await fetchExample(site(path), name);
        kept.openingNew(source);
        const message: PageToRuntime = { type: "open", project };
        worker.postMessage(message);
    } catch (e) {
        opening = undefined;
        task.failed(briefly(e));
    }
}

/** The example called `name` in the set served at `path`, with the chips it builds for. */
async function findExample(
    path: string,
    index: () => Promise<ExampleIndex>,
    name: string,
): Promise<{ path: string; targets?: string[] } | undefined> {
    const entry = (await index().catch(() => undefined))?.examples.find((example) => example.name === name);
    return entry === undefined ? undefined : { path, targets: entry.targets };
}

/** `all` is the set the chips an entry lists are compared with, for "All chips". */
function pickerExamples(
    index: ExampleIndex,
    open: (name: string, targets: string[] | undefined) => void,
    all: ExampleIndex = index,
): PickerExample[] {
    const chips = chipsOf(all);
    return index.examples.map((example) => ({
        path: example.name,
        title: example.title,
        description: example.description,
        detail: chips(example.targets),
        open: () => open(example.name, example.targets),
    }));
}

/**
 * How an entry shows the chips an example builds for: "All chips" when its
 * README lists every chip any example in the index lists, or none, and the
 * chip names otherwise. Linux is not a chip and is left out.
 */
function chipsOf(index: ExampleIndex): (targets: string[] | undefined) => string {
    const all = new Set<string>();
    for (const example of index.examples) {
        for (const target of example.targets ?? []) if (target !== "linux") all.add(target);
    }
    return (targets) => {
        const named = (targets ?? []).filter((target) => target !== "linux");
        if (targets === undefined || [...all].every((target) => named.includes(target))) {
            return "All chips";
        }
        return named.map(chipNameOfTarget).join(", ");
    };
}

/**
 * The way to start from a directory on this computer. It is not offered where
 * the browser has no File System Access API; the folders opened before are
 * among the kept projects.
 */
function localItems(reporter: Reporter, kept: KeptProjects): WelcomeItem[] {
    if (!localDirectoriesSupported()) return [];
    return [{ title: "Choose a folder…", open: () => void openLocalDirectory(reporter, kept) }];
}

/**
 * Open a directory on this computer that the user picks now, asking whether
 * to keep it in sync. The worker reads it and reports the open as a task of
 * its own.
 *
 * The picker gives read access only. Write access is asked for after the user
 * chooses to sync, from the click on the dialog's Open button, so a folder
 * copied once never asks for it.
 */
async function openLocalDirectory(reporter: Reporter, kept: KeptProjects): Promise<void> {
    try {
        const handle = await pickDirectory();
        const sync = handle && (await askToSync(handle.name));
        if (handle === undefined || sync === undefined) return;
        if (!(await allowedAccess(handle, sync))) {
            reporter.message("warn", `idfweb may not ${sync ? "change" : "read"} ${handle.name}.`);
            return;
        }
        await kept.openFolder(handle, sync);
    } catch (e) {
        reporter.message("error", `Could not open the folder: ${briefly(e)}`);
    }
}

/**
 * Set the tab title from the opened project: an example's last path component or
 * the folder's name, so several tabs can be told apart.
 */
function showProjectName(name: string): void {
    document.title = `${name.split("/").pop()} — ${siteTitle}`;
}

function briefly(e: unknown): string {
    return e instanceof Error ? e.message : String(e);
}

/**
 * Build and Run, which run the workbench's commands. Both are disabled while
 * either runs, since Run starts with a build, and Run is disabled while no
 * board is connected. A build started from the keyboard does not disable
 * them; the workbench refuses a second one.
 */
function buildButtons(
    reporter: Reporter,
    workbench: Workbench,
): { element: HTMLElement; setConnected(connected: boolean): void } {
    let running = false;
    let connected = false;
    const [buildButton, runButton] = [
        { label: "Build", icon: "tools", command: "idfweb.build" },
        { label: "Run", icon: "play", command: "idfweb.run" },
    ].map(({ label, icon, command }) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "idfweb-device-button";
        const glyph = document.createElement("span");
        glyph.className = `codicon codicon-${icon}`;
        glyph.setAttribute("aria-hidden", "true");
        button.append(glyph, label);
        button.addEventListener("click", () => void run(command));
        return button;
    }) as [HTMLButtonElement, HTMLButtonElement];
    const buildKey = workbench.command("idfweb.build")?.keys?.[0];
    buildButton.title = buildKey === undefined ? "Build the project" : `Build the project (${keyLabel(buildKey)})`;
    const render = () => {
        buildButton.disabled = running;
        runButton.disabled = running || !connected;
        runButton.title = connected
            ? "Build the project and flash it to the board"
            : "Connect a board to build the project and flash it";
    };
    const run = async (command: string) => {
        running = true;
        render();
        try {
            await workbench.execute(command);
        } catch (e) {
            reporter.message("error", `${command} failed: ${briefly(e)}`);
        } finally {
            running = false;
            render();
        }
    };
    render();
    // One element, so that the toolbar wraps them together.
    const element = document.createElement("span");
    element.className = "idfweb-device-group";
    element.append(buildButton, runButton);
    return {
        element,
        setConnected: (value) => {
            connected = value;
            render();
        },
    };
}

/**
 * A toolbar button that opens the quick input with one of the workbench's
 * commands, labeled with its key. A click while the box shows `prefix` closes
 * it. The box is read on pointerdown, before the button takes the focus.
 */
function commandButton(workbench: Workbench, icon: string, id: string, prefix: string): HTMLButtonElement {
    let open: string | undefined;
    const button = iconButton(icon, describeCommand(workbench, id), () => {
        if (open === prefix) workbench.hideQuick();
        else void workbench.execute(id);
        open = undefined;
    });
    button.addEventListener("pointerdown", () => {
        open = workbench.quickPrefix();
    });
    return button;
}

/** Go Back and Go Forward, disabled while there is no location to go to, as VS Code's title bar shows them. */
function navigationButtons(workbench: Workbench): HTMLButtonElement[] {
    const make = (icon: string, id: string) =>
        iconButton(icon, describeCommand(workbench, id), () => void workbench.execute(id));
    const back = make("arrow-left", "workbench.action.navigateBack");
    const forward = make("arrow-right", "workbench.action.navigateForward");
    const render = () => {
        const { back: canBack, forward: canForward } = workbench.navigation();
        back.disabled = !canBack;
        forward.disabled = !canForward;
    };
    workbench.onNavigation(render);
    render();
    return [back, forward];
}

/** The button that opens the keyboard shortcuts tab, with a keycap drawn at the codicons' size and stroke. */
function shortcutsButton(workbench: Workbench): HTMLButtonElement {
    const id = "workbench.action.openGlobalKeybindings";
    const button = document.createElement("button");
    button.type = "button";
    button.className = "idfweb-device-button idfweb-icon-button svg-icon-button";
    button.title = describeCommand(workbench, id);
    button.setAttribute("aria-label", button.title);
    button.innerHTML = `
        <svg viewBox="0 0 16 16" aria-hidden="true">
            <rect x="1.5" y="1.5" width="13" height="13" rx="2" />
            <rect x="4" y="3" width="8" height="6.5" rx="1" />
            <path d="M4.3 9.3 2 14M11.7 9.3 14 14" />
        </svg>`;
    button.addEventListener("click", () => void workbench.execute(id));
    return button;
}

/** A command's title and first key, for a tooltip: "Go to File… (⌘P)". */
function describeCommand(workbench: Workbench, id: string): string {
    const command = workbench.command(id);
    if (command === undefined) return id;
    const key = command.keys?.[0];
    return key === undefined ? command.title : `${command.title} (${keyLabel(key)})`;
}

/**
 * A button that shows or hides a side bar, drawn as VS Code's layout controls:
 * the side filled while it is shown and crossed out while it is hidden.
 */
function toggleButton(icon: string, toggle: () => void): { button: HTMLButtonElement; show(visible: boolean): void } {
    const button = iconButton(icon, "", toggle);
    return {
        button,
        show: (visible) => {
            button.classList.toggle(`codicon-${icon}`, visible);
            button.classList.toggle(`codicon-${icon}-off`, !visible);
            button.setAttribute("aria-pressed", String(visible));
        },
    };
}

function separator(): HTMLElement {
    const element = document.createElement("span");
    element.className = "idfweb-toolbar-separator";
    return element;
}

/** Pushes what follows it to the toolbar's right end. */
function spacer(): HTMLElement {
    const element = document.createElement("span");
    element.className = "idfweb-toolbar-spacer";
    return element;
}

/**
 * The divider between the workbench and the output panel. `changed` is called
 * when a drag ends and when the panes stack or stop stacking.
 */
function splitter(changed: () => void): {
    stacked: MediaQueryList;
    size(): Layout["split"];
    setSize(split: Layout["split"]): void;
} {
    const bar = splitterElement;
    // styles.css stacks the panes below this width.
    const stacked = window.matchMedia("(max-width: 760px)");
    const resize = (wanted: number) => {
        if (stacked.matches) {
            sideElement.style.height = `${Math.min(window.innerHeight - 120, Math.max(80, wanted))}px`;
            return;
        }
        const most = Math.max(MIN_PANE, window.innerWidth - MIN_PANE);
        sideElement.style.width = `${Math.min(most, Math.max(MIN_PANE, wanted))}px`;
    };
    // A size dragged in one arrangement means nothing in the other.
    stacked.addEventListener("change", () => {
        sideElement.style.width = "";
        sideElement.style.height = "";
        changed();
    });
    bar.addEventListener("pointerdown", (event: PointerEvent) => {
        bar.setPointerCapture(event.pointerId);
        document.body.classList.add("dragging");
        event.preventDefault();
    });
    bar.addEventListener("pointermove", (event: PointerEvent) => {
        if (!bar.hasPointerCapture(event.pointerId)) return;
        resize(
            stacked.matches
                ? window.innerHeight - event.clientY - bar.offsetHeight / 2
                : window.innerWidth - event.clientX - bar.offsetWidth / 2,
        );
    });
    const stop = (event: PointerEvent) => {
        if (!bar.hasPointerCapture(event.pointerId)) return;
        bar.releasePointerCapture(event.pointerId);
        document.body.classList.remove("dragging");
        changed();
    };
    bar.addEventListener("pointerup", stop);
    bar.addEventListener("pointercancel", stop);
    return {
        stacked,
        size: () => {
            const size = parseFloat(stacked.matches ? sideElement.style.height : sideElement.style.width);
            return Number.isFinite(size) ? { stacked: stacked.matches, size } : undefined;
        },
        setSize: (split) => {
            sideElement.style.width = "";
            sideElement.style.height = "";
            // The window may be smaller than when the size was stored.
            if (split?.stacked === stacked.matches && typeof split.size === "number") resize(split.size);
        },
    };
}

/** The page cannot run, and this is the only thing left to say. */
function fatal(message: string): void {
    const box = document.createElement("div");
    box.className = "fatal";
    box.textContent = message;
    sideElement.hidden = false;
    sideElement.appendChild(box);
}

void start();
